import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { coursesApi } from '@/api/courses';
import { usersApi, type AdminUser } from '@/api/users';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useIsMobile } from '@/hooks/useIsMobile';
import { assignTargets, type AssignTarget } from '@/lib/curatorCardActions';
import { lifecycleErrorText } from '@/lib/enrollmentActions';

/**
 * «Назначить ученика» куратору: найти ученика → выбрать его активную запись → группа этого куратора в том же курсе
 * или «лично» (только записи без группы).
 *
 * ⚠️ ГРУППУ ЗДЕСЬ НЕ СОЗДАЁМ. Нет группы куратора в курсе ученика — подсказка идти в «Группы»: заводить группу из
 * карточки значило бы плодить группы по одной на человека (как и в карточке ученика, `GroupPicker`).
 */
export function AssignStudentModal({ open, onClose, curatorId }: { open: boolean; onClose: () => void; curatorId: string }) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [student, setStudent] = useState<AdminUser | null>(null);
  const [enrollmentId, setEnrollmentId] = useState('');
  const [targetIdx, setTargetIdx] = useState('');

  // Поиск — после паузы в наборе, от двух букв: по каждой букве запрос к списку учеников был бы лишним.
  useEffect(() => {
    const id = setTimeout(() => setQuery(q.trim()), 300);
    return () => clearTimeout(id);
  }, [q]);

  const found = useQuery({
    queryKey: ['admin', 'students', 'assign-search', query],
    queryFn: () => usersApi.list({ role: 'student', q: query, limit: 20 }),
    enabled: open && query.length >= 2 && !student,
  });
  const enrollments = useQuery({
    queryKey: ['admin', 'student', student?.id, 'enrollments'],
    queryFn: () => usersApi.listUserEnrollments(student!.id),
    enabled: !!student,
  });
  const marathons = useQuery({
    queryKey: ['admin', 'student-detail', 'marathons'],
    queryFn: () => coursesApi.listAllMarathons({ limit: 100, template: 'false' }),
    enabled: !!student,
  });
  const titles = new Map((marathons.data?.items ?? []).map((m) => [m.id, m.title]));
  const active = (enrollments.data ?? []).filter((e) => e.status === 'active');
  const enrollment = active.find((e) => e.id === enrollmentId) ?? (active.length === 1 ? active[0] : undefined);

  const groups = useQuery({
    queryKey: ['admin', 'marathon', enrollment?.marathon_id, 'groups'],
    queryFn: () => usersApi.listGroups(enrollment!.marathon_id),
    enabled: !!enrollment,
  });
  const targets: AssignTarget[] = enrollment ? assignTargets(enrollment, groups.data?.items ?? [], curatorId) : [];
  const target = targetIdx === '' ? undefined : targets[Number(targetIdx)];

  const apply = useMutation({
    mutationFn: async () => {
      if (!enrollment || !target) return;
      if (target.kind === 'group') await usersApi.moveToGroup(enrollment.id, enrollment.group_id ?? null, target.groupId);
      else await usersApi.assignCurator(enrollment.id, curatorId);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'curator'] });
      qc.invalidateQueries({ queryKey: ['admin', 'curators'] });
      qc.invalidateQueries({ queryKey: ['admin', 'student'] });
      onClose();
    },
  });

  // На телефоне — внизу окна во весь экран (правило 3), на компьютере — под полями, как было.
  const buttons = (
    <div className={isMobile ? 'grid grid-cols-2 gap-2' : 'flex justify-end gap-2 pt-2'}>
      <Button variant="ghost" onClick={onClose} disabled={apply.isPending}>
        {t('common.cancel')}
      </Button>
      <Button onClick={() => apply.mutate()} loading={apply.isPending} disabled={!enrollment || !target}>
        {t('adminCuratorCard.apply')}
      </Button>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('adminCuratorCard.assign')}
      size="md"
      phoneFull
      footer={isMobile ? buttons : undefined}
    >
      <div className="space-y-3 text-sm">
        {!student ? (
          <>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('adminCuratorCard.searchStudent')} />
            {query.length >= 2 && found.data && found.data.items.length === 0 && (
              <p className="text-on-surface-variant">{t('adminCuratorCard.searchEmpty')}</p>
            )}
            {/* На телефоне окно само прокручивается — своей прокрутки у списка нет, строки по 44 px, почта под именем. */}
            <ul className={isMobile ? 'space-y-1' : 'max-h-64 space-y-1 overflow-y-auto'}>
              {(found.data?.items ?? []).map((u) => (
                <li key={u.id}>
                  <button
                    type="button"
                    className="w-full rounded-lg px-3 py-2 text-left hover:bg-surface-container-highest max-md:min-h-11"
                    onClick={() => setStudent(u)}
                  >
                    <span className="font-medium max-md:block">{u.full_name}</span>{' '}
                    <span className="text-on-surface-variant max-md:block max-md:text-xs">{u.email}</span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between gap-2">
              <p>
                <span className="font-medium">{student.full_name}</span>{' '}
                <span className="text-on-surface-variant">{student.email}</span>
              </p>
              <Button size="sm" variant="ghost" onClick={() => { setStudent(null); setEnrollmentId(''); setTargetIdx(''); }}>
                {t('adminCuratorCard.otherStudent')}
              </Button>
            </div>

            {enrollments.data && active.length === 0 && (
              <p className="text-warning">{t('adminCuratorCard.noActiveEnrollments')}</p>
            )}
            {active.length > 1 && (
              <label className="block">
                {t('adminDetail.course')}
                <select
                  value={enrollmentId}
                  onChange={(e) => { setEnrollmentId(e.target.value); setTargetIdx(''); }}
                  className="mt-1 w-full rounded-lg border border-outline bg-surface px-3 py-2"
                >
                  <option value="">{t('adminDetail.coursePick')}</option>
                  {active.map((e) => (
                    <option key={e.id} value={e.id}>{titles.get(e.marathon_id) ?? e.marathon_id.slice(0, 8)}</option>
                  ))}
                </select>
              </label>
            )}
            {enrollment && active.length === 1 && (
              <p className="text-on-surface-variant">{titles.get(enrollment.marathon_id) ?? enrollment.marathon_id.slice(0, 8)}</p>
            )}

            {enrollment && groups.data && targets.length === 0 && (
              <p className="text-warning">{t('adminCuratorCard.noTargets')}</p>
            )}
            {enrollment && targets.length > 0 && (
              <label className="block">
                {t('adminCuratorCard.target')}
                <select
                  value={targetIdx}
                  onChange={(e) => setTargetIdx(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-outline bg-surface px-3 py-2"
                >
                  <option value="">{t('adminCuratorCard.targetPick')}</option>
                  {targets.map((tg, i) => (
                    <option key={tg.kind === 'group' ? tg.groupId : 'personal'} value={String(i)}>
                      {tg.kind === 'group' ? t('adminCuratorCard.viaGroup', { name: tg.name }) : t('adminCuratorCard.viaPersonal')}
                    </option>
                  ))}
                </select>
                {enrollment.group_id && target?.kind === 'group' && (
                  <span className="mt-1 block text-xs text-on-surface-variant">{t('adminCuratorCard.moveWarning')}</span>
                )}
              </label>
            )}
          </>
        )}

        {apply.error && <p className="text-error">{lifecycleErrorText(apply.error, t)}</p>}
        {!isMobile && buttons}
      </div>
    </Modal>
  );
}
